// The toggleable landing sections, per venue type. Admin show/hide + (later)
// reorder / per-card images / titles all key off these. Keep the keys in sync
// across the admin editor, the branding preview, and the real venue landing.
import type { VenueAppConfig } from "./types";
import { isBookingVenue } from "./venue-type";

export type SectionDef = { key: string; label: string };

// Loyalty is a BOTH-types feature (gated by the venue's loyaltyEnabled flag,
// not venue type, NFC taps earn loyalty on hospitality and studio alike).
const HOSPITALITY: SectionDef[] = [
  { key: "noticeboard", label: "Noticeboard" },
  { key: "benefits", label: "Member benefits" },
  { key: "events", label: "Events" },
  { key: "loyalty", label: "Loyalty" },
];

const STUDIO: SectionDef[] = [
  { key: "noticeboard", label: "Noticeboard" },
  { key: "benefits", label: "Member benefits" },
  { key: "sessions", label: "Bookable sessions" },
  // Studios can also sell ticketed events, only shown when they have some.
  { key: "events", label: "Events" },
  { key: "loyalty", label: "Loyalty" },
];

// Saunas / wellness venues: same shape as STUDIO but sauna-native labels.
const WELLNESS: SectionDef[] = [
  { key: "noticeboard", label: "Noticeboard" },
  { key: "benefits", label: "Member benefits" },
  { key: "sessions", label: "Sessions" },
  // Wellness venues can also sell ticketed events (retreats, workshops).
  { key: "events", label: "Events" },
  { key: "loyalty", label: "Loyalty" },
];

export function sectionsForType(archetype?: string): SectionDef[] {
  if (archetype === "STUDIO") return STUDIO;
  if (archetype === "WELLNESS") return WELLNESS;
  return HOSPITALITY;
}

export function isSectionHidden(
  cfg: VenueAppConfig | null | undefined,
  key: string,
): boolean {
  return !!cfg?.hiddenSections?.includes(key);
}

// All section keys for the type in the admin-configured order (custom order
// first, then any not-yet-ordered defaults). Includes hidden ones, used by
// the admin list so reordering preserves position regardless of visibility.
export function mergeOrder(
  archetype: string | undefined,
  order: string[] | undefined,
): string[] {
  const all = sectionsForType(archetype).map((s) => s.key);
  const ord = order ?? [];
  return [...ord.filter((k) => all.includes(k)), ...all.filter((k) => !ord.includes(k))];
}

// The keys to render, in order, after dropping hidden + not-applicable ones.
export function orderedVisibleSections(
  archetype: string | undefined,
  cfg: VenueAppConfig | null | undefined,
  opts: { loyaltyEnabled?: boolean; hasEvents?: boolean } = {},
): string[] {
  const booking = isBookingVenue(archetype);
  return mergeOrder(archetype, cfg?.sectionOrder).filter((k) => {
    if (cfg?.hiddenSections?.includes(k)) return false;
    if (k === "loyalty" && !opts.loyaltyEnabled) return false;
    // Events is a hospitality default; for booking venues (studio/wellness)
    // only show it once the venue actually has published events (avoids an
    // empty section).
    if (k === "events" && booking && !opts.hasEvents) return false;
    return true;
  });
}
